import { useConfigStore } from "../../Stores/Config";
import { useHelpCenterStore } from "../../Stores/HelpCenter";
import { useSessionStore } from "../../Stores/Session";
import { GenericHttpClient } from "./config/http.generic.client";

// The AI answer and sources endpoints run a knowledge search plus an LLM call
// before responding, so they regularly exceed the 4s default timeout. Give them
// time to finish and never retry: an aborted call is still processed server-side,
// so a retry only queues another expensive AI call for the same question.
const AI_SEARCH_TIMEOUT = 30000;

const HELPCENTER_TIMEOUT = 15000;

export class HttpHelpCenterService extends GenericHttpClient<any> {
  static _instance: HttpHelpCenterService;
  static getInstance(): HttpHelpCenterService {
    if (this._instance == null) {
      this._instance = new HttpHelpCenterService("/v3/shared/helpcenter");
    }
    return this._instance;
  }

  protected injectToken(
    headers: Record<string, string | boolean>,
  ): Record<string, string | boolean> {
    const { session, sdkKey } = useSessionStore.getState();
    const { config } = useConfigStore.getState();

    if (config.defaultDomainHelpCenter) {
      headers["api-token"] = config.defaultDomainHelpCenter;
    } else if (sdkKey) {
      headers["api-token"] = sdkKey;
    }

    if (session && session.gleapId) {
      headers["gleap-id"] = session.gleapId;
    }

    if (session && session.gleapHash) {
      headers["gleap-hash"] = session.gleapHash;
    }

    const { password } = useHelpCenterStore.getState();
    if (password) {
      headers["password"] = password;
    }

    return headers;
  }

  async getCollections(lang: string): Promise<any> {
    const response = await this.get<any[]>(`${this.path}/collections`, {
      params: { lang },
      timeout: HELPCENTER_TIMEOUT,
    });
    return response.data;
  }

  async getCollection(collectionId: string, lang: string): Promise<any> {
    const response = await this.get<any>(
      `${this.path}/collections/${collectionId}`,
      { params: { lang }, timeout: HELPCENTER_TIMEOUT },
    );
    return response.data;
  }

  async getArticle(articleId: string, lang: string): Promise<any> {
    const response = await this.get<any>(`${this.path}/articles/${articleId}`, {
      params: { lang },
      timeout: HELPCENTER_TIMEOUT,
    });
    return response.data;
  }

  async searchArticles(searchTerm: string, lang: string): Promise<any> {
    const response = await this.get<any[]>(`${this.path}/search`, {
      params: { searchTerm, lang },
      timeout: HELPCENTER_TIMEOUT,
    });
    return response.data;
  }

  async getAIAnswer(question: string, lang: string): Promise<any> {
    const response = await this.get<any>(`${this.path}/answer`, {
      params: { question, lang },
      timeout: AI_SEARCH_TIMEOUT,
      retry: false,
    });
    return response.data;
  }

  async getSources(searchTerm: string, lang: string): Promise<any> {
    const response = await this.get<any>(`${this.path}/sources`, {
      params: { searchTerm, lang },
      timeout: AI_SEARCH_TIMEOUT,
      retry: false,
    });
    return response.data;
  }

  async trackArticleView(articleId: string): Promise<void> {
    try {
      await this.post(`${this.path}/track-article-view`, {
        articleID: articleId,
      }, {
        timeout: HELPCENTER_TIMEOUT,
      });
    } catch {}
  }

  async submitArticleFeedback(
    articleId: string,
    lang: string,
    type: string,
  ): Promise<void> {
    try {
      await this.put(`${this.path}/articles/${articleId}/feedback`, {
        type,
      }, {
        params: { lang },
        timeout: HELPCENTER_TIMEOUT,
      });
    } catch {}
  }
}
